import { AbstractBluetoothAdapter } from './AbstractBluetoothAdapter';
import { ATBaseClient } from '@src/services/ATBaseClient';

const atBaseClient = new ATBaseClient();

/**
 * Client for making cross frame calls to bluetoothService in Seaward.
 */
export class CrossFrameBluetoothAdapter extends AbstractBluetoothAdapter {
    // public functions

    /**
     * Calls Bluetooth pair method with the provided config.
     *
     * @param {object} The config object to send.
     * @return {Promise} The promise resolves when the device gets paired and
     *  rejects if there's an error.
     */
    public pair(config: any): Promise<any> {
        console.log('ATBase BluetoothService pair config ' + JSON.stringify(config));
        return CrossFrameBluetoothAdapter.makeATBaseCallForBluetoothListener('pairWithOptions', config);
    }

    /**
     * Calls Bluetooth connect method with the provided config.
     *
     * @param {object} The config object to send.
     * @return {Promise} The promise resolves when the device becomes connected and
     *  rejects if there's an error.
     */
    public connect(config: any): Promise<any> {
        console.log('ATBase BluetoothService connect config ' + JSON.stringify(config));
        return CrossFrameBluetoothAdapter.makeATBaseCallForBluetoothListener('connect', config);
    }

    /**
     * Calls Bluetooth getDeviceInfo method with the provided config.
     *
     * @param {object} The config object to send.
     * @return {Promise} The promise resolves when device information is returned and
     *  rejects if there's an error.
     */
    public getDeviceInfo(config: any): Promise<any> {
        console.log('ATBase BluetoothService getDeviceInfo config ' + JSON.stringify(config));
        return CrossFrameBluetoothAdapter.makeATBaseCallForBluetoothListener('getDeviceInfo', config);
    }

    /**
     * Calls Bluetooth pairAndConnect method which activates the UI, with the provided config.
     *
     * @param {object} The config object to send.
     * @return {Promise} The promise resolves when the device is successfully paired and connected
     *  through UI and rejects if there's an error.
     */
    public pairAndConnect(config: any): Promise<any> {
        console.log('ATBase BluetoothService pairAndConnect config ' + JSON.stringify(config));
        return CrossFrameBluetoothAdapter.makeATBaseCallForBluetoothListener('pairAndConnect', config);
    }

    /**
     * Calls Bluetooth getLastPairedDevice method which returns the last paired device of a device type.
     *
     * @param {object} The config object to send.
     * @return {Promise} The promise resolves when the last paired device is retrieved from local storage and returned.
     */
    public getLastPairedDevice(config: any): Promise<any> {
        console.log('ATBase BluetoothService getLastPairedDevice config ' + JSON.stringify(config));
        return CrossFrameBluetoothAdapter.makeATBaseCallForBluetoothListener('getLastPairedDevice', config);
    }

    /**
     * Calls Bluetooth setLastPairedDevice method which sets the last paired device of a device type.
     *
     * @param {object} The config object to send.
     * @return {Promise} The promise resolves when the last paired device is set in local storage.
     */
    public setLastPairedDevice(config: any): Promise<any> {
        console.log('ATBase BluetoothService setLastPairedDevice config ' + JSON.stringify(config));
        return CrossFrameBluetoothAdapter.makeATBaseCallForBluetoothListener('setLastPairedDevice', config);
    }

    /**
     * Calls Bluetooth clearLastPairedDevice to clear last paired device of a device type from local storage.
     *
     * @param {object} The config object to send.
     * @return {Promise} The promise resolves the value is removed from local storage.
     */
    public clearLastPairedDevice(config: any): Promise<any> {
        console.log('ATBase BluetoothService clearLastPairedDevice config ' + JSON.stringify(config));
        return CrossFrameBluetoothAdapter.makeATBaseCallForBluetoothListener('clearLastPairedDevice', config);
    }

    /**
     * Calls Bluetooth clearLastPairedDevice to clear last paired device of all device types
     *
     * @param {object} The config object to send.
     * @return {Promise} The promise resolves when all last paired devices are cleared from local storage.
     */
    public clearAllLastPairedDevices(config: any): Promise<any> {
        console.log('ATBase BluetoothService clearAllLastPairedDevices config ' + JSON.stringify(config));
        return CrossFrameBluetoothAdapter.makeATBaseCallForBluetoothListener('clearAllLastPairedDevices', config);
    }

    /**
     * sends command and receives data from the device for serial connection
     * @param config
     * @returns {*}
     */
    public requestDataSerialComm(config) {
        console.log('ATBase BluetoothSerialCommService requestDataSerialComm config ' + JSON.stringify(config));
        return CrossFrameBluetoothAdapter.makeATBaseCallForBluetoothSerialCommListener('requestDataSerialComm', config);
    }

    /**
     * sends data to the device for serial data connection
     * @param config
     * @returns {*}
     */
    public writeDataSerialComm(config) {
        console.log('ATBase BluetoothSerialCommService writeDataSerialComm config ' + JSON.stringify(config));
        return CrossFrameBluetoothAdapter.makeATBaseCallForBluetoothSerialCommListener('writeDataSerialComm', config);
    }

    /**
     * disconnect the serially connected device
     * @param config
     * @returns {*}
     */
    public disconnectSerialComm(config) {
        console.log('ATBase BluetoothSerialCommService disconnectSerialComm config ' + JSON.stringify(config));
        return CrossFrameBluetoothAdapter.makeATBaseCallForBluetoothSerialCommListener('disconnectSerialComm', config);
    }

    /**
     * changes the Standard charset for serial data connection
     * @param config
     * @returns {*}
     */
    public changeCharsetSerialComm(config) {
        console.log('ATBase BluetoothSerialCommService changeCharsetSerialComm config ' + JSON.stringify(config));
        return CrossFrameBluetoothAdapter.makeATBaseCallForBluetoothSerialCommListener('changeCharsetSerialComm', config);
    }

    /**
     * Connects to the bluetooth low energy device.
     * @param config
     * @returns {*}
     */
    public connectBle(config: any): Promise<any> {
        console.log('ATBase BluetoothLowEnergyService connectBle config ' + JSON.stringify(config));
        return CrossFrameBluetoothAdapter.makeATBaseCallForBluetoothLowEnergyServiceListener('connectBle', config);
    }

    /**
     * Disconnects from the bluetooth low energy device.
     * @param config
     * @returns {*}
     */
    public disconnectBle(config: any): Promise<any> {
        console.log('ATBase BluetoothLowEnergyService disconnectBle config ' + JSON.stringify(config));
        return CrossFrameBluetoothAdapter.makeATBaseCallForBluetoothLowEnergyServiceListener('disconnectBle', config);
    }

    /**
     * Reads the characteristic value from the connected bluetooth low energy device.
     * @param config
     * @returns {*}
     */
    public readCharacteristicBle(config: any): Promise<any> {
        console.log('ATBase BluetoothLowEnergyService readCharacteristicBle config ' + JSON.stringify(config));
        return CrossFrameBluetoothAdapter.makeATBaseCallForBluetoothLowEnergyServiceListener('readCharacteristicBle', config);
    }

    /**
     * Writes the characteristic value data to the bluetooth low energy device.
     * @param config
     * @returns {*}
     */
    public writeCharacteristicBle(config: any): Promise<any> {
        console.log('ATBase BluetoothLowEnergyService writeCharacteristicBle config ' + JSON.stringify(config));
        return CrossFrameBluetoothAdapter.makeATBaseCallForBluetoothLowEnergyServiceListener('writeCharacteristicBle', config);
    }

    public getPrinterDPI(config: any): Promise<any> {
        console.log('CrossFrameBluetoothAdapter: getPrinterDPI not yet implemented');
        return Promise.reject(new Error('Method not implemented'));
    }

    // Private functions

    private static makeATBaseCallForBluetoothListener(action: any, parameters: any) {
        console.log('ATBase BluetoothService makeATBaseCall action', action, 'parameters: ', parameters);
        return CrossFrameBluetoothAdapter.makeATBaseCall('bluetoothListener', action, parameters);
    }

    private static makeATBaseCallForBluetoothSerialCommListener(action, parameters) {
        console.log('ATBase BluetoothService makeATBaseCall action', action, 'parameters: ', parameters);
        return CrossFrameBluetoothAdapter.makeATBaseCall('bluetoothSerialCommListener', action, parameters);
    }

    private static makeATBaseCallForBluetoothLowEnergyServiceListener(action: any, parameters: any) {
        console.log('ATBase BluetoothLowEnergyService makeATBaseCall action', action, 'parameters: ', parameters);
        return CrossFrameBluetoothAdapter.makeATBaseCall('bluetoothLowEnergyServiceListener', action, parameters);
    }

    /**
     * Makes the cross frame call to Seaward.
     */
    private static makeATBaseCall(listener: string, action: any, parameters: any) {
        return atBaseClient.call(listener, action, parameters);
    }
}
